import { isModuleNotFoundError } from "@tanstack/router-core";
import { useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { Button } from "@fp/ui/components/base/buttons/button";
import { PageLoading } from "@fp/ui/components/base/page-layout/page-layout";
import { isAxiosError } from "axios";
import { useEffect } from "react";

function isOutOfReach(error: unknown) {
	if (!isAxiosError(error)) return false;

	const status = error.response?.status;

	return status === 403 || status === 404;
}

function outcomeFor(error: unknown) {
	if (isModuleNotFoundError(error)) return "reload";
	if (isOutOfReach(error)) return "dashboard";

	return "report";
}

export function RouteError({ error, reset }: ErrorComponentProps) {
	const router = useRouter();
	const outcome = outcomeFor(error);

	useEffect(() => {
		if (outcome === "dashboard") {
			router.navigate({ to: "/dashboard", replace: true });
		}
	}, [outcome, router]);

	if (outcome === "reload") {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="text-center max-w-md px-8 py-10 bg-white border border-gray-200 rounded shadow-sm">
					<h1 className="text-xl font-semibold text-gray-900 mb-2">
						This page has been updated
					</h1>
					<p className="text-gray-600 mb-6">
						A new version is available. Please reload to continue.
					</p>
					<Button color="primary-neutral" onClick={() => window.location.reload()}>
						Reload
					</Button>
				</div>
			</div>
		);
	}

	if (outcome === "dashboard") {
		return <PageLoading />;
	}

	return (
		<div className="flex items-center justify-center min-h-screen">
			<div className="text-center max-w-md px-8 py-10 bg-white border border-gray-200 rounded shadow-sm">
				<h1 className="text-xl font-semibold text-gray-900 mb-2">
					Something went wrong
				</h1>
				<p className="text-gray-600 mb-6">
					An unexpected error occurred. Please try again.
				</p>
				<Button color="primary-neutral" onClick={reset}>
					Try again
				</Button>
			</div>
		</div>
	);
}
